<template>
  <div class="reset-password-page">
    <div class="reset-password-container">
      <div class="reset-password-header">
        <h1 class="page-title">Set New Password</h1>
        <p class="page-subtitle">
          Enter your new password below.
        </p>
      </div>

      <div class="reset-password-card">
        <div v-if="success" class="success-message">
          <div class="success-icon">✓</div>
          <h2>Password Reset Successful!</h2>
          <p>Your password has been changed successfully. You can now sign in with your new password.</p>
          <router-link to="/" class="sign-in-link">
            Sign In
          </router-link>
        </div>

        <div v-else>
          <div v-if="error" class="error-message">
            <strong>Oops!</strong> {{ error }}
          </div>

          <div v-if="loading" class="loading-state">
            <div class="spinner"></div>
            <p>Resetting your password...</p>
          </div>

          <form v-else @submit.prevent="handleResetPassword" class="reset-password-form">
            <div class="form-group">
              <label for="password">New Password</label>
              <input
                type="password"
                id="password"
                v-model="password"
                required
                placeholder="Enter your new password"
                class="form-input"
                minlength="6"
              />
              <small class="form-hint">Password must be at least 6 characters long</small>
            </div>

            <div class="form-group">
              <label for="passwordConfirmation">Confirm New Password</label>
              <input
                type="password"
                id="passwordConfirmation"
                v-model="passwordConfirmation"
                required
                placeholder="Confirm your new password"
                class="form-input"
                minlength="6"
              />
            </div>

            <div v-if="passwordMismatch" class="error-message">
              Passwords do not match
            </div>

            <button 
              type="submit" 
              class="submit-button"
              :disabled="passwordMismatch || !password || !passwordConfirmation"
            >
              Reset Password
            </button>

            <div class="back-to-login">
              <router-link to="/">
                ← Back to Sign In
              </router-link>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { resetPassword } from '../../services/authService.js'
import { showErrorModal } from '../../services/errorHandler.js'

export default {
  name: 'ResetPassword',
  data() {
    return {
      password: '',
      passwordConfirmation: '',
      loading: false,
      error: null,
      success: false,
      resetToken: null
    }
  },
  computed: {
    passwordMismatch() {
      return this.password && this.passwordConfirmation && this.password !== this.passwordConfirmation
    }
  },
  mounted() {
    // Get reset token from URL query parameter
    this.resetToken = this.$route.query.reset_password_token || this.$route.query.token
    
    if (!this.resetToken) {
      this.error = 'Invalid or missing reset token. Please request a new password reset link.'
    }
  },
  methods: {
    async handleResetPassword() {
      if (this.passwordMismatch) {
        this.error = 'Passwords do not match'
        return
      }

      if (!this.resetToken) {
        this.error = 'Invalid reset token. Please request a new password reset link.'
        return
      }

      this.loading = true
      this.error = null

      try {
        await resetPassword(this.resetToken, this.password, this.passwordConfirmation)
        this.success = true
      } catch (error) {
        this.error = error.message || 'Failed to reset password. Please try again or request a new reset link.'
        showErrorModal([this.error])
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

<style scoped>
.reset-password-page {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  background: var(--color-background-main);
}

.reset-password-container {
  width: 100%;
  max-width: 500px;
}

.reset-password-header {
  text-align: center;
  margin-bottom: 30px;
}

.page-title {
  font-size: 2rem;
  color: var(--color-text-primary);
  margin-bottom: 10px;
  font-weight: 600;
}

.page-subtitle {
  color: var(--color-text-secondary);
  font-size: 1rem;
  line-height: 1.5;
}

.reset-password-card {
  background: var(--color-surface-light);
  border-radius: 12px;
  padding: 40px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
}

.success-message {
  text-align: center;
  padding: 20px;
}

.success-icon {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background-color: var(--color-success-main);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2rem;
  margin: 0 auto 20px;
  font-weight: bold;
}

.success-message h2 {
  color: var(--color-text-primary);
  margin-bottom: 15px;
  font-size: 1.5rem;
}

.success-message p {
  color: var(--color-text-secondary);
  margin-bottom: 25px;
  line-height: 1.6;
}

.sign-in-link {
  display: inline-block;
  padding: 12px 24px;
  background-color: var(--color-primary-main);
  color: var(--color-text-inverse);
  text-decoration: none;
  border-radius: 8px;
  font-weight: 600;
  transition: all var(--transition-normal);
}

.sign-in-link:hover {
  background-color: var(--color-primary-dark);
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(212, 165, 169, 0.3);
}

.error-message {
  background-color: var(--color-error-light);
  color: var(--color-error-dark);
  padding: 15px;
  border-radius: 6px;
  margin-bottom: 20px;
  border: 1px solid var(--color-error-main);
}

.loading-state {
  text-align: center;
  padding: 40px 20px;
  color: var(--color-text-secondary);
}

.spinner {
  border: 3px solid var(--color-border-light);
  border-top: 3px solid var(--color-primary-main);
  border-radius: 50%;
  width: 40px;
  height: 40px;
  animation: spin 1s linear infinite;
  margin: 0 auto 20px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.reset-password-form {
  display: flex;
  flex-direction: column;
  gap: 25px;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.form-group label {
  font-weight: 600;
  color: var(--color-text-primary);
  font-size: 0.95rem;
}

.form-input {
  width: 100%;
  padding: 12px;
  border: 2px solid var(--color-border-light);
  border-radius: 6px;
  font-size: 1rem;
  font-family: inherit;
  background-color: var(--color-surface-light);
  color: var(--color-text-primary);
  transition: border-color var(--transition-normal);
  box-sizing: border-box;
}

.form-input:focus {
  outline: none;
  border-color: var(--color-primary-main);
}

.form-hint {
  color: var(--color-text-light);
  font-size: 0.85rem;
  margin-top: -5px;
}

.submit-button {
  width: 100%;
  padding: 14px 20px;
  background-color: var(--color-primary-main);
  color: var(--color-text-inverse);
  border: none;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all var(--transition-normal);
}

.submit-button:hover:not(:disabled) {
  background-color: var(--color-primary-dark);
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(212, 165, 169, 0.3);
}

.submit-button:active:not(:disabled) {
  transform: translateY(0);
}

.submit-button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.back-to-login {
  text-align: center;
  margin-top: 10px;
}

.back-to-login a {
  color: var(--color-primary-main);
  text-decoration: none;
  font-size: 0.9rem;
  transition: color var(--transition-normal);
}

.back-to-login a:hover {
  color: var(--color-primary-dark);
  text-decoration: underline;
}
</style>
